import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MessageCircle, Sparkles, X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import { siteContent } from "@/content/site";

const t = siteContent.testimonials;
const RELATION_OPTIONS = t.relationOptions;
const RELATION_LABEL = t.relationShortLabel;

const AVATAR_COLORS = ["#1F4E5F", "#E8A33D", "#5BAE71", "#0E2A36", "#A85A2A"];

function initialsOf(name) {
  if (!name) return "··";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColorFor(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/testimonials");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/testimonials");
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-20 lg:py-28 bg-[#E1EEEA]/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8A33D]"
            data-testid="testimonials-eyebrow"
          >
            {items.length === 0 ? t.eyebrowEmpty : t.eyebrowFilled}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A36]">
            {items.length === 0 ? (
              <>
                {t.headingEmptyLead}{" "}
                <em className="italic font-medium text-[#1F4E5F]">
                  {t.headingEmptyItalic}
                </em>
              </>
            ) : (
              <>
                {t.headingFilledLead}{" "}
                <em className="italic font-medium text-[#1F4E5F]">
                  {t.headingFilledItalic}
                </em>
              </>
            )}
          </h2>
          <p className="mt-4 text-[#3D5560] text-base leading-relaxed">
            {t.intro}{" "}
            <span className="font-semibold text-[#1F4E5F]">{t.introHighlight}</span>{" "}
            {t.introTail}
          </p>
        </div>

        {loading ? (
          <div
            data-testid="testimonials-loading"
            className="flex items-center justify-center py-16 text-[#6A8290] text-sm"
          >
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t.loadingLabel}
          </div>
        ) : items.length === 0 ? (
          <EmptyState onShare={() => setOpen(true)} />
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-5">
              {items.map((it, i) => (
                <TestimonialCard t={it} i={i} key={it.id} />
              ))}
            </div>
            <div className="mt-10 flex justify-center">
              <button
                data-testid="testimonials-share-cta"
                onClick={() => setOpen(true)}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] font-semibold text-sm shadow-[0_10px_28px_rgba(31,78,95,0.25)] transition-all"
              >
                <MessageCircle className="w-4 h-4 transition-transform group-hover:-rotate-6" />
                {t.shareCta}
              </button>
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <ShareModal
            onClose={() => setOpen(false)}
            onSubmitted={() => {
              fetchItems();
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function TestimonialCard({ t: it, i }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      className="bg-white border border-[#C9DDD6] rounded-3xl p-7 flex flex-col hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(15,56,32,0.08)] transition-all"
      data-testid={`testimonial-${it.id}`}
    >
      <div
        className="flex gap-0.5 text-[#E8A33D] mb-4"
        aria-label={`${it.rating} out of 5 stars`}
      >
        {[...Array(5)].map((_, k) => (
          <Star
            key={k}
            className={`w-4 h-4 ${
              k < (it.rating || 0)
                ? "fill-[#E8A33D]"
                : "fill-transparent text-[#E8A33D]/40"
            }`}
          />
        ))}
      </div>
      <blockquote className="font-display text-lg text-[#0E2A36] leading-snug flex-1">
        <span aria-hidden>“</span>
        {it.quote}
        <span aria-hidden>”</span>
      </blockquote>
      <figcaption className="mt-6 flex items-center gap-3 pt-5 border-t border-[#C9DDD6]">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center font-display font-bold text-white text-sm"
          style={{ background: avatarColorFor(it.id || it.name) }}
        >
          {initialsOf(it.name)}
        </div>
        <div>
          <div className="font-semibold text-[#0E2A36] text-sm">{it.name}</div>
          <div className="text-xs text-[#6A8290]">
            {RELATION_LABEL[it.relation] || RELATION_LABEL.other}
            {it.city ? ` · ${it.city}` : ""}
          </div>
        </div>
      </figcaption>
    </motion.figure>
  );
}

function EmptyState({ onShare }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      data-testid="testimonials-empty"
      className="relative max-w-3xl mx-auto rounded-3xl overflow-hidden border border-[#C9DDD6] bg-white p-8 sm:p-10"
    >
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#E8A33D]/15 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[#5BAE71]/15 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] bg-[#FBF1DC] text-[#7A571A] border border-[#E8A33D]/30">
          <Sparkles className="w-3 h-3" /> {t.emptyBadge}
        </div>

        <h3 className="mt-5 font-display text-2xl sm:text-3xl font-bold text-[#0E2A36]">
          {t.emptyTitle}
        </h3>
        <p className="mt-3 text-[#3D5560] leading-relaxed">{t.emptyBody}</p>

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          {t.emptyBullets.map((b, i) => (
            <Bullet key={i}>{b}</Bullet>
          ))}
        </div>

        <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center">
          <button
            data-testid="testimonials-empty-cta"
            onClick={onShare}
            className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] font-semibold text-sm shadow-[0_10px_28px_rgba(31,78,95,0.25)] transition-all"
          >
            <MessageCircle className="w-4 h-4 transition-transform group-hover:-rotate-6" />
            {t.emptyCta}
          </button>
          <span className="text-xs text-[#6A8290]">
            {t.emptyOrLineBefore}
            <a className="text-[#1F4E5F] underline" href={`mailto:${t.emptyOrEmail}`}>
              {t.emptyOrEmail}
            </a>
            {t.emptyOrLineAfter}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2 text-sm text-[#0E2A36]">
      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#5BAE71] shrink-0" />
      <span>{children}</span>
    </div>
  );
}

/* ============= Share modal ============= */
function ShareModal({ onClose, onSubmitted }) {
  const m = t.modal;
  const [name, setName] = useState("");
  const [relation, setRelation] = useState(RELATION_OPTIONS[0]?.value || "adult_child");
  const [city, setCity] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [quote, setQuote] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const ratingValue = hoverRating || rating;

  const remaining = 1000 - quote.length;
  const tooShort = quote.trim().length < 10;
  const tooLong = quote.length > 1000;

  const canSubmit = useMemo(
    () =>
      name.trim().length >= 1 &&
      rating >= 1 &&
      rating <= 5 &&
      !tooShort &&
      !tooLong &&
      consent &&
      !submitting,
    [name, rating, tooShort, tooLong, consent, submitting]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/testimonials", {
        name: name.trim(),
        relation,
        city: city.trim() || null,
        rating,
        quote: quote.trim(),
        consent_publish: true,
      });
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      data-testid="testimonial-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="absolute inset-0 bg-[#0E2A36]/55 backdrop-blur-sm"
        onClick={() => !submitting && onClose()}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ type: "spring", damping: 22, stiffness: 220 }}
        className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-[0_30px_80px_rgba(14,42,54,0.35)] border border-[#C9DDD6]"
      >
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#E1EEEA] flex items-center justify-between px-6 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E8A33D]">
              {m.eyebrow}
            </p>
            <h3 className="font-display text-lg font-bold text-[#0E2A36]">{m.title}</h3>
          </div>
          <button
            data-testid="testimonial-modal-close"
            onClick={() => !submitting && onClose()}
            className="w-9 h-9 rounded-full hover:bg-[#F4FAF8] inline-flex items-center justify-center text-[#3D5560]"
            aria-label={m.closeAria}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {success ? (
          <div data-testid="testimonial-success" className="px-6 py-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#5BAE71]/15 inline-flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-[#2D7A4A]" />
            </div>
            <h4 className="font-display text-xl font-bold text-[#0E2A36]">
              {m.successTitle}
            </h4>
            <p className="mt-2 text-sm text-[#3D5560] max-w-md mx-auto">{m.successBody}</p>
            <button
              data-testid="testimonial-success-close"
              onClick={onClose}
              className="mt-6 inline-flex items-center px-5 py-2.5 rounded-full bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] font-semibold text-sm"
            >
              {m.successCta}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-6 py-5 space-y-5">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-2">
                {m.ratingLabel}
              </label>
              <div className="flex items-center gap-1.5" data-testid="testimonial-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    data-testid={`testimonial-rating-${n}`}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onFocus={() => setHoverRating(n)}
                    onBlur={() => setHoverRating(0)}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    className="p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/40"
                  >
                    <Star
                      className={`w-8 h-8 transition-transform ${
                        n <= ratingValue
                          ? "fill-[#E8A33D] text-[#E8A33D] scale-100"
                          : "fill-transparent text-[#E8A33D]/40"
                      } ${n === hoverRating ? "scale-110" : ""}`}
                    />
                  </button>
                ))}
                <span className="ml-2 text-xs text-[#6A8290]">
                  {rating === 0 ? m.ratingPrompt : `${rating}/5`}
                </span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={m.nameLabel} htmlFor="t-name">
                <input
                  id="t-name"
                  data-testid="testimonial-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  placeholder={m.namePlaceholder}
                  className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[#0E2A36] focus:outline-none focus:border-[#1F4E5F]"
                />
              </Field>
              <Field label={m.relationLabel} htmlFor="t-relation">
                <select
                  id="t-relation"
                  data-testid="testimonial-relation"
                  value={relation}
                  onChange={(e) => setRelation(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[#0E2A36] focus:outline-none focus:border-[#1F4E5F]"
                >
                  {RELATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label={m.cityLabel} htmlFor="t-city">
              <input
                id="t-city"
                data-testid="testimonial-city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={120}
                placeholder={m.cityPlaceholder}
                className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[#0E2A36] focus:outline-none focus:border-[#1F4E5F]"
              />
            </Field>

            <Field
              label={m.quoteLabel}
              htmlFor="t-quote"
              hint={
                tooLong
                  ? `${-remaining} characters over the limit.`
                  : `${quote.trim().length < 10 ? m.quoteMinHint : ""} ${
                      remaining < 100 ? `${remaining} left` : ""
                    }`.trim()
              }
            >
              <textarea
                id="t-quote"
                data-testid="testimonial-quote"
                rows={4}
                value={quote}
                onChange={(e) => setQuote(e.target.value)}
                placeholder={m.quotePlaceholder}
                className="w-full px-4 py-3 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[#0E2A36] focus:outline-none focus:border-[#1F4E5F] resize-y leading-relaxed"
              />
            </Field>

            <label
              data-testid="testimonial-consent"
              className="flex items-start gap-2.5 text-sm text-[#3D5560] cursor-pointer select-none"
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-[#C9DDD6] accent-[#1F4E5F]"
              />
              <span>{m.consent}</span>
            </label>

            {error && (
              <div
                data-testid="testimonial-error"
                className="rounded-xl border border-[#F0C4C4] bg-[#FBEDED] text-[#A63333] text-sm px-4 py-3"
              >
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E1EEEA]">
              <button
                type="button"
                onClick={() => !submitting && onClose()}
                className="px-4 py-2 rounded-full text-sm font-semibold text-[#3D5560] hover:bg-[#F4FAF8]"
                data-testid="testimonial-cancel"
              >
                {m.cancel}
              </button>
              <button
                type="submit"
                data-testid="testimonial-submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {submitting ? m.submitting : m.submit}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children, htmlFor, hint }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5"
      >
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[11px] text-[#6A8290]">{hint}</p> : null}
    </div>
  );
}
